/**
 * RevenueTrendChart - 12-month revenue trend visualization
 *
 * Features:
 * - Area chart with gradient fill
 * - MRR primary line with optional ARR
 * - Custom tooltip matching app design
 * - Responsive container
 */
import { cn, chartA11yProps } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { RevenueTrendDataPoint } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface RevenueTrendChartProps {
  data: RevenueTrendDataPoint[];
  showArr?: boolean;
  className?: string;
}

// ============================================
// Formatters
// ============================================

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

function formatMonth(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short' });
}

// ============================================
// Custom Tooltip
// ============================================

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-lg">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
        {label ? new Date(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : ''}
      </p>
      <div className="space-y-1.5">
        {payload.map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-300">
                {entry.name}
              </span>
            </div>
            <span className="text-xs font-semibold text-slate-900 dark:text-white tabular-nums">
              {formatCurrency(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function RevenueTrendChart({
  data,
  showArr = false,
  className,
}: RevenueTrendChartProps) {
  if (!data.length) {
    return (
      <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <p className="text-sm text-slate-500">No data available</p>
        </CardContent>
      </Card>
    );
  }

  // Non-null assertions are safe: the `!data.length` early return above guarantees both ends exist.
  const first = data[0]!;
  const last = data[data.length - 1]!;
  const chartLabel =
    `Revenue trend over ${data.length} month${data.length === 1 ? '' : 's'}, ${formatMonth(first.date)} to ${formatMonth(last.date)}: ` +
    `monthly recurring revenue went from ${formatCurrency(first.mrr)} to ${formatCurrency(last.mrr)}` +
    (showArr ? `, annual recurring revenue from ${formatCurrency(first.arr)} to ${formatCurrency(last.arr)}` : '') +
    `, ending at ${last.activeSubscriptions.toLocaleString()} active subscriptions.`;

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Revenue Trend</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              {...chartA11yProps(chartLabel)}
            >
              <defs>
                <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="arrGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e2e8f0"
                className="dark:stroke-slate-700"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                tickFormatter={formatMonth}
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={false}
                dy={8}
              />

              <YAxis
                tickFormatter={(value) => formatCurrency(value)}
                tick={{ fontSize: 11, fill: '#64748b' }}
                tickLine={false}
                axisLine={false}
                width={60}
              />

              <Tooltip content={<CustomTooltip />} />

              <Legend
                verticalAlign="top"
                height={36}
                formatter={(value: string) => (
                  <span className="text-xs text-slate-600 dark:text-slate-300">{value}</span>
                )}
              />

              <Area
                type="monotone"
                dataKey="mrr"
                name="MRR"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#mrrGradient)"
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: '#10b981',
                  strokeWidth: 2,
                  fill: '#fff',
                }}
              />

              {showArr && (
                <Area
                  type="monotone"
                  dataKey="arr"
                  name="ARR"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fill="url(#arrGradient)"
                  dot={false}
                  activeDot={{
                    r: 4,
                    stroke: '#3b82f6',
                    strokeWidth: 2,
                    fill: '#fff',
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function RevenueTrendChartSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-2">
        <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
      </CardContent>
    </Card>
  );
}
